import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { Paper } from '@mui/material';
import axios from 'axios';
import useAuthz from 'hooks/useAuthz';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useAlert } from 'react-alert';
import { useNavigate } from 'react-router';
import ClipLoader from 'react-spinners/ClipLoader';
import ActionsDevisRenderer from '../../components/AgGrid/ActionsDevisRenderer';
import AgGrid from '../../components/AgGrid/AgGrid';
import RequestPassDateRenderer from '../../components/AgGrid/RequestPassDateRenderer';
import MyButton from '../../components/Layout/MyButton';
import TopBar from '../../components/Layout/TopBar';
import { formatAmount, logError } from '../../components/Utils/FormatData';
import { URL_API } from '../../utils/globalConfig';
import ModalCreateOrEditDevis from './ModalCreateOrEditDevis';
import ModalCreateOrEditFacturation from './ModalCreateOrEditFacturation';
import SelectorTypeFacturation from './SelectorTypeFacturation';
import { PROFIL } from '../../constants/enum';

const ListeDevisFactures = (props) => {
    const currentPage = 'facturation';
    const [requestPassId, setRequestPassId] = useState(new URLSearchParams(location.search).get('id') || null);
    const alert = useAlert();
    const navigate = useNavigate();
    const [authz, user, profil] = useAuthz();
    const [reloadData, setReloadData] = useState(false);
    const devis = [
        {
            headerName: 'n° dossier',
            field: 'dossier_id',
            sortable: true,
            flex: 1,
            minWidth: 150,
            filter: 'agNumberColumnFilter',
        },
        { headerName: 'n° devis', field: 'id', sortable: true, flex: 1, minWidth: 85, filter: 'agNumberColumnFilter' },
        {
            headerName: 'réf ddt',
            field: 'dossier.metadata.reference.numDossier',
            flex: 2,
            minWidth: 250,
            sortable: true,
            valueGetter: (params) => (params.data.dossier.metadata.reference.numDossier ? params.data.dossier.metadata.reference.numDossier : '-'),
            filter: 'agTextColumnFilter',
        },
        {
            headerName: 'centre',
            field: 'dossier.metadata.centers',
            flex: 2.2,
            cellStyle: { 'justify-content': 'flex-start' },
            valueGetter: (params) => {
                let centers = params.data.dossier.metadata.complInfo.refCtr;
                if (!centers) {
                    return '-';
                }
                let centersNames = '';
                centers.map((center) => {
                    if (center) {
                        centersNames += center.name + ', ';
                    }
                });

                centersNames = centersNames.slice(0, -2);

                return centersNames;
            },
        },
        {
            headerName: 'type dossier',
            field: 'dossier.metadata.reference.typeDemande',
            flex: 2,
            minWidth: 80,
            sortable: true,
            valueGetter: (params) => (params.data.dossier.metadata.reference.typeDemande === 'superieur' ? 'PS' : 'PA'),
            filter: 'agTextColumnFilter',
        },
        {
            headerName: 'date envoi devis',
            field: 'dates',
            flex: 2,
            sortable: true,
            minWidth: 200,
            valueGetter: (params) => {
                const raw = params.data?.elements_factures?.creationDate;
                if (!raw) return null;

                // Gère les deux formats : avec T ou avec espace
                const normalized = raw.replace(' ', 'T');
                return new Date(normalized);

            },
            valueFormatter: (params) => {
                if (!params.value) return '-';
                return moment(params.value).format('DD/MM/YYYY HH:mm');
            },
            filter: 'agDateColumnFilter',
        },
        {
            headerName: 'date retour devis',
            field: 'dates',
            flex: 2,
            sortable: true,
            minWidth: 200,
            valueGetter: (params) => {
                const raw = params.data?.elements_factures?.returnDate;
                if (!raw) return null;

                // Gère les deux formats : avec T ou avec espace
                const normalized = raw.replace(' ', 'T');
                return new Date(normalized);

            },
            valueFormatter: (params) => {
                if (!params.value) return '-';
                return moment(params.value).format('DD/MM/YYYY HH:mm');
            },
            filter: 'agDateColumnFilter',
        },
        {
            headerName: 'mandataire',
            field: 'dossier.metadata.complementIdentite.mandataire',
            flex: 2,
            sortable: true,
            minWidth: 200,
            valueGetter: (params) => (params.data.dossier.metadata.complementIdentite.mandataire.transporteur ? params.data.dossier.metadata.complementIdentite.mandataire.transporteur : '-'),
            filter: 'agTextColumnFilter',
        },
        {
            headerName: 'petitionnaire',
            field: 'dossier.metadata.complementIdentite.transporteur',
            flex: 2,
            sortable: true,
            minWidth: 200,
            valueGetter: (params) => (params.data.dossier.metadata.complementIdentite.transporteur ? params.data.dossier.metadata.complementIdentite.transporteur : '-'),
            filter: 'agTextColumnFilter',
        },
        {
            headerName: 'tiers facturé',
            field: 'dossier.metadata.complementIdentite.tiers',
            flex: 2,
            sortable: true,
            minWidth: 200,
            valueGetter: (params) => (params.data.dossier.metadata.complementIdentite.tiers.transporteur ? params.data.dossier.metadata.complementIdentite.tiers.transporteur : '-'),
            filter: 'agTextColumnFilter',
        },
        {
            headerName: 'montant du devis',
            field: 'montant',
            sortable: true,
            flex: 1,
            minWidth: 170,
            valueGetter: (params) =>
                params.data.montant ? formatAmount(params.data.montant, 2, '€') : 0,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: 'Statut',
            field: 'status',
            sortable: true,
            flex: 1,
            minWidth: 200,
            valueGetter: (params) => {
                switch (params.data.elements_factures?.status?.statutDevis) {
                    case 'accepted':
                        return !params.data.elements_factures?.status?.pdfAvailable ? 'En cours de constitution' : 'Accepté';
                    case 'refused':
                        return 'Refusé';
                    default:
                        return !params.data.elements_factures?.status?.pdfAvailable ? 'En cours de constitution' : 'En attente de réponse';
                }
            },
            filter: 'agTextColumnFilter',
            cellStyle: { 'justify-content': 'flex-start' },
            pinned: 'right',
        },
        {
            headerName: 'actions',
            field: '',
            width: 150,
            sortable: false,
            cellRenderer: 'actionsDevisRenderer',
            cellRendererParams: {
                setReloadData: setReloadData,
            },
            pinned: 'right',
            cellStyle: { 'justify-content': 'flex-start' },
        }];


    const factures = [
        {
            headerName: 'n° dossier',
            field: 'dossier_id',
            sortable: true,
            flex: 1,
            minWidth: 150,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: 'n° Commande de Vente',
            field: 'id',
            sortable: true,
            flex: 1,
            minWidth: 85,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: 'N° devis',
            field: 'devis',
            sortable: true,
            flex: 1,
            minWidth: 150,
            cellStyle: { 'justify-content': 'flex-start' },
            valueGetter: (params) => {
                let devis = params.data.elements_factures.devis;

                if (devis) {
                    return devis[0];
                }

                return '-';
            },
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: 'réf ddt',
            field: 'dossier.metadata.reference.numDossier',
            flex: 2,
            minWidth: 150,
            sortable: true,
            valueGetter: (params) => (params.data.dossier.metadata.reference.numDossier ? params.data.dossier.metadata.reference.numDossier : '-'),
            filter: 'agTextColumnFilter',
        },
        {
            headerName: 'tiers facturé',
            field: 'dossier.metadata.complementIdentite.tiers',
            flex: 2,
            sortable: true,
            minWidth: 200,
            valueGetter: (params) => (params.data.dossier.metadata.complementIdentite.tiers.transporteur ? params.data.dossier.metadata.complementIdentite.tiers.transporteur : '-'),
            filter: 'agTextColumnFilter',
        },
        {
            headerName: 'N° client SAP',
            field: 'dossier.metadata.complementIdentite.tiers',
            flex: 2,
            sortable: true,
            minWidth: 200,
            valueGetter: (params) => (params.data.dossier.metadata.complementIdentite.tiers.numSap ? params.data.dossier.metadata.complementIdentite.tiers.numSap : '-'),
            filter: 'agTextColumnFilter',
        },
        {
            headerName: 'Date/Heure envoi',
            field: 'dates',
            flex: 2,
            sortable: true,
            minWidth: 200,
            valueGetter: (params) => {
                const raw = params.data?.elements_factures?.creationDate;
                if (!raw) return null;

                // Gère les deux formats : avec T ou avec espace
                const normalized = raw.replace(' ', 'T');
                return new Date(normalized);

            },
            valueFormatter: (params) => {
                if (!params.value) return '-';
                return moment(params.value).format('DD/MM/YYYY HH:mm');
            },
            filter: 'agDateColumnFilter',
        },
        {
            headerName: 'N° Commande client',
            field: 'numCommande',
            flex: 2,
            sortable: true,
            minWidth: 200,
            valueGetter: (params) => {
                let facturations = params.data;

                if (facturations.elements_factures.status.refCommande) {
                    return facturations.elements_factures.status.refCommande;
                }

                return '-';

            },
            filter: 'agTextColumnFilter',
        },
        {
            headerName: 'N° TVA Intracommunautaire',
            field: 'dossier.metadata.complementIdentite.tiers2',
            flex: 2,
            sortable: true,
            minWidth: 200,
            valueGetter: (params) => (params.data.dossier.metadata.complementIdentite.tiers.numTva ? params.data.dossier.metadata.complementIdentite.tiers.numTva : '-'),
            filter: 'agTextColumnFilter',
        },
        {
            headerName: 'centre',
            field: 'dossier.metadata.centers',
            flex: 2.2,
            cellStyle: { 'justify-content': 'flex-start' },
            valueGetter: (params) => {
                let centers = params.data.dossier.metadata.complInfo.refCtr;
                if (!centers) {
                    return '-';
                }
                let centersNames = '';
                centers.map((center) => {
                    if (center) {
                        centersNames += center.name + ', ';
                    }
                });

                centersNames = centersNames.slice(0, -2);

                return centersNames;
            },
        },
        {
            headerName: 'type dossier',
            field: 'dossier.metadata.reference.typeDemande',
            flex: 2,
            minWidth: 80,
            sortable: true,
            valueGetter: (params) => (params.data.dossier.metadata.reference.typeDemande === 'superieur' ? 'PS' : 'PA'),
            filter: 'agTextColumnFilter',
        },
        {
            headerName: 'montant HT',
            field: 'montant',
            sortable: true,
            flex: 1,
            minWidth: 150,
            valueGetter: (params) =>
                params.data.montant ? formatAmount(params.data.montant, 2, '€') : 0,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: 'Statut',
            field: 'status',
            sortable: true,
            flex: 1,
            minWidth: 200,
            valueGetter: (params) => {
                if (!params.data.elements_factures?.status?.pdfAvailable) {
                    return 'En cours de constitution';
                } else if (params.data.elements_factures?.status?.statutDevis === 'sap') {
                    if (params.data.elements_factures?.status?.sapSent && params.data.elements_factures?.status?.sapSent === true) {
                        return 'Facturée';
                    }
                    return 'Envoyée';
                } else if (params.data.elements_factures?.status?.statutDevis === 'failSap') {
                    return 'Échouée';
                } else {
                    return 'Validée';
                }
            },
            filter: 'agTextColumnFilter',
            cellStyle: { 'justify-content': 'flex-start' },
            pinned: 'right',
        },
        {
            headerName: 'actions',
            field: '',
            minWidth: 120,
            sortable: false,
            cellRenderer: 'actionsDevisRenderer',
            cellRendererParams: {
                setReloadData: setReloadData,
            },
            pinned: 'right',
            cellStyle: { 'justify-content': 'flex-start' },
        }];
    const [isLoading, setIsLoading] = useState(true);
    const [datas, setDatas] = useState([]);
    const [openDevisModal, setOpenDevisModal] = useState(false);
    const [openFacturationModal, setOpenFacturationModal] = useState(false);
    const [agGridOptions, setAgGridOptions] = useState({
        suppressCellSelection: true,
        suppressHorizontalScroll: false,
        animateRows: true,
    });
    const frameworkComponents = {
        actionsDevisRenderer: ActionsDevisRenderer,
        requestPassDateRenderer: RequestPassDateRenderer,
    };
    const [columns, setColumns] = useState(devis);
    const [filters, setFilters] = useState(null);
    const [billType, setBillType] = useState('devis');
    const [pageFullyLoaded, setPageFullyLoaded] = useState(false);

    //#region useEffect

    useEffect(() => {
        if (profil.profil === 6) {
            alert.error(`Vous n'êtes pas autorisé à accéder à la liste des facturations`);
            navigate('/');
        } else {
            document.title = (billType === 'devis') ? 'TE - Liste des devis' : 'TE - Liste des factures';
            setColumns(billType === 'devis' ? devis : factures);
            setPageFullyLoaded(true);
        }
    }, []);

    useEffect(() => {
        let localStorageFilters = JSON.parse(window.localStorage.getItem('filters'));
        if (!localStorageFilters || !localStorageFilters[currentPage]) {
            updateLocalStorageFilters({
                requestType: billType,
            });
        } else {
            if (new URLSearchParams(location.search).get('type')) {
                setBillType(new URLSearchParams(location.search).get('type'));
            } else {
                setBillType(localStorageFilters[currentPage].requestType);
            }
        }

        loadDatas(billType);
    }, [pageFullyLoaded]);

    useEffect(() => {
        document.title = (billType === 'devis') ? 'TE - Liste des devis' : 'TE - Liste des factures';
        setColumns(billType === 'devis' ? devis : factures);

        if (pageFullyLoaded) {
            loadDatas(billType);
        }
    }, [billType]);

    useEffect(() => {
        let requestPassIdUrl = new URLSearchParams(location.search).get('id');
        setRequestPassId(requestPassIdUrl);
        if (requestPassId !== null) {
            setFilters({ dossier_id: requestPassId });
            handleOnChange('devis');
            setRequestPassId(null);
        }
    }, [datas]);

    useEffect(() => {
        if (reloadData) {
            loadDatas(billType);
            setReloadData(false);
        }
    }, [reloadData]);

    //#endregion

    //#region Functions
    const updateLocalStorageFilters = (values) => {
        window.localStorage.setItem('filters', JSON.stringify({
            ...JSON.parse(window.localStorage.getItem('filters')),
            [currentPage]: values,
        }));
    };

    const handleOnChange = (requestType) => {
        if (pageFullyLoaded) {
            setBillType(requestType === 'devis' ? 'devis' : 'facture');
            updateLocalStorageFilters({
                requestType: requestType,
            });
        }
    };
    const handleCreateNew = () => {
        if (billType === 'devis') {
            setOpenDevisModal(true);
        } else {
            setOpenFacturationModal(true);
        }
    };

    //#endregion

    //#region API

    const loadDatas = (requestType) => {
        setIsLoading(true);

        if (profil.profil === 5) {
            axios({
                method: 'get',
                url: URL_API + '/facturation/list-by-user',
                params: { raison_sociale: user.raison_sociale, type: requestType },
            })
            .then((response) => {
                // response.data.forEach((devis) => {
                //     devis.dates = [];
                //     axios({
                //         method: 'get',
                //         url: URL_API + '/demande-passage-tracing/history-by-request-pass-id',
                //         params: { id: devis.dossier_id, userAction: user.user_id },
                //     }).then((response) => {
                //         devis.dates = response.data;
                //     });
                // });
                setIsLoading(false);
                setDatas(response.data);
            })
            .catch((error) => {
                setIsLoading(false);
                logError(alert, error);
            });
        } else {
            axios({ method: 'get', url: URL_API + '/facturation/full-list', params: { type: requestType } })
            .then((response) => {
                // response.data.forEach((devis) => {
                //     devis.dates = [];
                //     axios({
                //         method: 'get',
                //         url: URL_API + '/demande-passage-tracing/history-by-request-pass-id',
                //         params: { id: devis.dossier_id, userAction: user.user_id },
                //     }).then((response) => {
                //         devis.dates = response.data;
                //     });
                // });
                setIsLoading(false);
                setDatas(response.data);
            })
            .catch((error) => {
                setIsLoading(false);
                logError(alert, error);
            });
        }
    };

    //#endregion

    return (
        <div style={{ height: '100%' }}>
            <TopBar title={billType === 'devis' ? 'Liste des devis' : 'Liste des factures'}
                    className='topbar-contenu topbar-contenu-orange' color={props.params.color}
                    buttons={(profil.profil === PROFIL.ADMINISTRATEUR || profil.profil === PROFIL.GESTIONNAIRE || (profil.profil === PROFIL.INTERVENANT && billType !== 'devis')) &&
                        <div className='topbar-buttons'>
                            <MyButton onClick={() => handleCreateNew()} icon={faPlus}
                                      label={billType === 'devis' ? 'Nouveau devis' : 'Nouvelle facture'}
                                      sx={{ backgroundColor: '#b76618 !important' }} />
                        </div>
                    }
                    radioSelector={
                        <SelectorTypeFacturation value={pageFullyLoaded && billType} leftValue='Devis'
                                                 rightValue='Factures' profil={profil}
                                                 onChange={(e) => handleOnChange(e)} />
                    }
            />

            <div className='row' style={{ margin: 0, overflow: 'hidden' }}>
                <ClipLoader color='#0094ca' loading={isLoading} size={75}
                            css={'display: flex; margin: 25px;justify-content: center;'} />
                {!isLoading && datas && (
                    <Paper style={{ height: '100%', width: '100%', borderTop: '1px solid #b76618' }}>
                        <AgGrid
                            gridName='ag-grid-liste-devis-factures'
                            rowData={datas}
                            columnDefs={columns}
                            rowHeight={40}
                            pagination={false}
                            frameworkComponents={frameworkComponents}
                            gridOptions={agGridOptions}
                            setGridOptions={setAgGridOptions}
                            sortable={false}
                            filters={filters}
                            type={billType}
                            color='orange'
                        />
                    </Paper>
                )}
            </div>
            {(profil.profil === PROFIL.ADMINISTRATEUR || profil.profil === PROFIL.GESTIONNAIRE || PROFIL.INTERVENANT) &&
                <>
                    {(profil.profil === PROFIL.ADMINISTRATEUR || profil.profil === PROFIL.GESTIONNAIRE) &&
                        <ModalCreateOrEditDevis openModal={openDevisModal} setOpenModal={setOpenDevisModal}
                                                color={'#0094ca'} user={user}
                                                dossier_id={new URLSearchParams(location.search).get('id')}
                                                setReloadData={setReloadData} />
                    }
                    <ModalCreateOrEditFacturation openModal={openFacturationModal}
                                                  setOpenModal={setOpenFacturationModal} color={'#0094ca'} user={user}
                                                  setReloadData={setReloadData} />
                </>
            }
        </div>
    );
};

export default ListeDevisFactures;
